import { useCallback, useEffect, useMemo, useState } from 'react';
import translate from 'translate';
import { Button, Modal, ModalHeader } from 'components/UI/new';
import { Spinner } from 'components/UI/Loading/Spinner';
import Icon from 'components/UI/Icons/Icon';
import { colors } from 'style/colors';
import { ContentfulRenderer } from 'components';
import { isDocument } from 'utils/isDocument';
import { Document } from '@contentful/rich-text-types';
import { useLaunchSeawarePlatformContext } from 'contexts/LaunchSeawarePlatformContext';
import { useNavigate } from 'react-router';
import styles from './LaunchPlatform.module.scss';
import useSeawareMessageHidden from './useSeawareMessageHidden';
import { checkUserExistsInSeaware } from './utils';

const LoadingContent = () => (
  <div className={styles.loadingContainer}>
    <p>{translate('Seaware_UserCheckLoadingText')}</p>
    <div className={styles.spinnerContainer}>
      <Spinner />
    </div>
  </div>
);

const ErrorContent = () => {
  const { contactInfo } = useLaunchSeawarePlatformContext();

  return (
    <div className={styles.errorContainer}>
      <div className={styles.errorHeader}>
        <Icon
          iconType="circleWithExclamation"
          color={colors.shared.warmGray6}
          width={24}
          height={24}
        />
        <h3 className={styles.errorTitle}>
          {translate('Seaware_ActivationRequired')}
        </h3>
      </div>
      <div className={styles.errorMessage}>
        <p className={styles.errorText}>
          {translate('Seaware_ActivationRequiredDescription')}
        </p>
        <p className={styles.errorText}>
          {translate('Seaware_ContactUsToEnableAccess')}
        </p>
      </div>
      <div className={styles.contactContainer}>
        {(contactInfo?.phone || contactInfo?.openingHours) && (
          <div className={styles.contactColumn}>
            {contactInfo.phone && (
              <>
                <p className={styles.contactLabel}>
                  {translate('General_Phone')}:
                </p>
                <a
                  href={`tel:${contactInfo?.phone}`}
                  className={styles.contactLink}
                >
                  {contactInfo?.phone}
                </a>
              </>
            )}
            {contactInfo?.openingHours && (
              <div className={styles.operatingHours}>
                {isDocument(contactInfo.openingHours) ? (
                  <ContentfulRenderer>
                    {contactInfo.openingHours as Document}
                  </ContentfulRenderer>
                ) : (
                  <p>{contactInfo.openingHours}</p>
                )}
              </div>
            )}
          </div>
        )}
        {contactInfo?.email && (
          <div className={styles.contactColumn}>
            <p className={styles.contactLabel}>{translate('General_Email')}:</p>
            <a
              href={`mailto:${contactInfo?.email}`}
              className={styles.contactLink}
            >
              {contactInfo?.email}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

interface ILaunchPlatformProps {
  onClose: () => void;
  isOpen: boolean;
}

export const LaunchPlatformModal = ({
  onClose,
  isOpen,
}: ILaunchPlatformProps) => {
  const navigate = useNavigate();
  const [isLoadingUserCheck, setIsLoadingUserCheck] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const { isSeawareMessageHidden, handleCookieChange } =
    useSeawareMessageHidden();
  const [doNotShowAgainValue, setDoNotShowAgainValue] = useState(false);

  const handleLaunchPlatform = useCallback(
    async (autoLaunch = false) => {
      setIsLoadingUserCheck(true);
      if (!autoLaunch) {
        handleCookieChange(doNotShowAgainValue.toString());
      }
      try {
        const userExists = await checkUserExistsInSeaware();
        if (userExists) {
          window.open(process.env.REACT_APP_SEAWARE_TOUCH_ENDPOINT, '_blank');
          onClose();
        } else {
          setIsErrorModalOpen(true);
        }
      } catch (error) {
        setIsErrorModalOpen(true);
      }

      setIsLoadingUserCheck(false);
    },
    [doNotShowAgainValue, handleCookieChange, onClose]
  );

  const handleRedirectToTraining = useCallback(() => {
    navigate('/hx-training');
    onClose();
  }, [onClose, navigate]);

  const modalContent = useMemo(() => {
    if (isLoadingUserCheck) return <LoadingContent />;
    if (isErrorModalOpen) return <ErrorContent />;

    return (
      <>
        <div className={styles.messageContainer}>
          <div className={styles.buttonContainer}>
            <Button
              variant="secondary"
              size="normal"
              icon="newTab"
              iconPosition="right"
              onClick={handleRedirectToTraining}
            >
              {translate('Seaware_TrainingAndTutorials')}
            </Button>
            <Button
              variant="primary"
              size="normal"
              onClick={() => handleLaunchPlatform()}
            >
              {translate('Seaware_LaunchPlatform')}
            </Button>
          </div>

          <div className={styles.checkboxContainer}>
            <input
              type="checkbox"
              id="dontShowAgain"
              className={styles.checkbox}
              checked={doNotShowAgainValue}
              onChange={(event) => setDoNotShowAgainValue(event.target.checked)}
            />
            <label htmlFor="dontShowAgain" className={styles.checkboxLabel}>
              {translate('General_IUnderstand')}
            </label>
          </div>
        </div>
      </>
    );
  }, [
    doNotShowAgainValue,
    handleLaunchPlatform,
    handleRedirectToTraining,
    isErrorModalOpen,
    isLoadingUserCheck,
  ]);

  useEffect(() => {
    if (isSeawareMessageHidden) {
      handleLaunchPlatform(true);
    }
  }, [isSeawareMessageHidden, handleLaunchPlatform]);

  return (
    <Modal
      id="seaware-branch-launch-platform-modal"
      onClose={onClose}
      hideCloseButton={isLoadingUserCheck}
      isOpen={isOpen}
    >
      <ModalHeader title={translate('Seaware_AvailabilityNewBookingHeading')} />
      {modalContent}
    </Modal>
  );
};
